import { AnimatePresence, motion } from "framer-motion";
import { Sparkles, X, Volume2, Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { generateTTS, ttsUrl } from "@/lib/api";
import { TTS_VOICE_BY_LANG } from "@/i18n";
import { usePlayer } from "@/context/PlayerContext";

export default function GhostHint({ open, message, onClose, autoSpeak = false }) {
  const { t, i18n } = useTranslation();
  const { player } = usePlayer();
  const audioRef = useRef(null);
  const [ttsLoading, setTtsLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const autoSpokeForRef = useRef(null);

  const handleSpeak = async () => {
    if (playing) {
      audioRef.current?.pause();
      setPlaying(false);
      return;
    }
    setTtsLoading(true);
    try {
      const lang = (i18n.language || "fr").slice(0, 2);
      const voice = TTS_VOICE_BY_LANG[lang] || "fable";
      const res = await generateTTS(message, voice, 1.0, lang, player?.id || null);
      const url = ttsUrl(res.url);
      if (!audioRef.current) audioRef.current = new Audio();
      audioRef.current.src = url;
      audioRef.current.onended = () => setPlaying(false);
      audioRef.current.onpause = () => setPlaying(false);
      await audioRef.current.play();
      setPlaying(true);
    } catch (e) {
      // silent fail
    } finally {
      setTtsLoading(false);
    }
  };

  useEffect(() => {
    if (open && autoSpeak && message && autoSpokeForRef.current !== message) {
      autoSpokeForRef.current = message;
      handleSpeak();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, autoSpeak, message]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.95 }}
          transition={{ type: "spring", stiffness: 180, damping: 18 }}
          className="fixed bottom-8 right-8 z-40 max-w-sm"
          data-testid="ghost-hint-panel"
        >
          <div className="glass-strong rounded-3xl p-5 shadow-2xl">
            <div className="flex items-start gap-4">
              <div className="ghost-float text-5xl select-none" aria-hidden>👻</div>
              <div className="flex-1">
                <div className="flex items-center gap-2 text-amber-200/90 text-xs uppercase tracking-widest font-bold mb-1">
                  <Sparkles className="w-3.5 h-3.5" />
                  <span>{t("play.hint")}</span>
                </div>
                <p className="font-serif text-stone-100 text-base leading-relaxed">{message}</p>
                <button
                  onClick={handleSpeak}
                  className="mt-3 inline-flex items-center gap-1.5 text-xs text-fuchsia-300 hover:text-fuchsia-200 transition-colors"
                  data-testid="ghost-hint-speak"
                >
                  {ttsLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Volume2 className="w-3.5 h-3.5" />}
                  <span>{playing ? t("play.speak_stop") : ttsLoading ? t("play.speak_loading") : t("play.speak_hint")}</span>
                </button>
              </div>
              <button
                onClick={onClose}
                className="text-stone-400 hover:text-stone-100 transition-colors"
                data-testid="ghost-hint-close"
                aria-label={t("play.close_hint_aria")}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
